/**
 * Google Analytics 4 Provider
 * Enhanced Ecommerce implementation
 */

import { getDefaultCurrency, isDebugMode } from '../config'

import type {
  AddPaymentInfoParams,
  AddShippingInfoParams,
  AddToCartParams,
  AddToWishlistParams,
  AnalyticsProduct,
  AnalyticsProvider,
  ApplyPromoCodeParams,
  BeginCheckoutParams,
  LoginParams,
  PurchaseParams,
  RefundParams,
  RemoveFromCartParams,
  SearchParams,
  SelectItemParams,
  SignUpParams,
  ViewCartParams,
  ViewItemListParams,
  ViewItemParams,
} from '../types'

// ============================================================================
// GA4 Item type
// ============================================================================

interface GA4Item {
  item_id: string
  item_name: string
  price?: number
  quantity?: number
  item_brand?: string
  item_category?: string
  item_variant?: string
  item_sku?: string
  discount?: number
  item_list_name?: string
  item_list_id?: string
  index?: number
}

/**
 * Преобразование товара в формат GA4
 */
function toGA4Item (product: AnalyticsProduct, index?: number): GA4Item {
  return {
    item_id: product.id,
    item_name: product.name,
    price: product.price,
    quantity: product.quantity ?? 1,
    ...(product.brand && { item_brand: product.brand }),
    ...(product.category && { item_category: product.category }),
    ...(product.variant && { item_variant: product.variant }),
    ...(product.sku && { item_sku: product.sku }),
    ...(product.discount && { discount: product.discount }),
    ...(product.listName && { item_list_name: product.listName }),
    ...(product.listId && { item_list_id: product.listId }),
    ...(index !== undefined && { index }),
  }
}

/**
 * Безопасный вызов gtag
 */
function safeGtag (
  command: 'event',
  eventName: string,
  params?: Record<string, unknown>
): void {
  try {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag(command, eventName, params)
      
      if (isDebugMode()) {
        console.log(`[GA4] ${eventName}`, params)
      }
    }
  }
  catch (error) {
    console.error('[GA4] Error sending event:', error)
  }
}

/**
 * Google Analytics 4 Provider
 */
export const ga4Provider: AnalyticsProvider = {
  name: 'Google Analytics 4',

  init () {
    // GA4 инициализируется через gtag.js в index.html
    if (isDebugMode()) {
      console.log('[GA4] Provider initialized')
    }
  },

  isAvailable (): boolean {
    return typeof window !== 'undefined' && typeof window.gtag === 'function'
  },

  // ========================================================================
  // Ecommerce события
  // ========================================================================

  viewItemList ({ listId, listName, items }: ViewItemListParams) {
    safeGtag('event', 'view_item_list', {
      item_list_id: listId,
      item_list_name: listName,
      items: items.map((item, index) => toGA4Item(item, index)),
    })
  },

  viewItem ({ item, currency = getDefaultCurrency(), value }: ViewItemParams) {
    safeGtag('event', 'view_item', {
      currency,
      value: value ?? item.price,
      items: [ toGA4Item(item) ],
    })
  },

  selectItem ({ listId, listName, item }: SelectItemParams) {
    safeGtag('event', 'select_item', {
      item_list_id: listId,
      item_list_name: listName,
      items: [ toGA4Item(item) ],
    })
  },

  addToCart ({ item, currency = getDefaultCurrency(), value }: AddToCartParams) {
    safeGtag('event', 'add_to_cart', {
      currency,
      value: value ?? item.price * (item.quantity ?? 1),
      items: [ toGA4Item(item) ],
    })
  },

  removeFromCart ({ item, currency = getDefaultCurrency(), value }: RemoveFromCartParams) {
    safeGtag('event', 'remove_from_cart', {
      currency,
      value: value ?? item.price * (item.quantity ?? 1),
      items: [ toGA4Item(item) ],
    })
  },

  viewCart ({ items, currency = getDefaultCurrency(), value }: ViewCartParams) {
    const totalValue = value ?? items.reduce((sum, item) => 
      sum + item.price * (item.quantity ?? 1), 0
    )
    
    safeGtag('event', 'view_cart', {
      currency,
      value: totalValue,
      items: items.map((item, index) => toGA4Item(item, index)),
    })
  },

  beginCheckout ({ items, currency = getDefaultCurrency(), value, coupon }: BeginCheckoutParams) {
    const totalValue = value ?? items.reduce((sum, item) => 
      sum + item.price * (item.quantity ?? 1), 0
    )
    
    safeGtag('event', 'begin_checkout', {
      currency,
      value: totalValue,
      items: items.map((item, index) => toGA4Item(item, index)),
      ...(coupon && { coupon }),
    })
  },

  addShippingInfo ({ items, currency = getDefaultCurrency(), value, coupon, shippingTier }: AddShippingInfoParams) {
    const totalValue = value ?? items.reduce((sum, item) => 
      sum + item.price * (item.quantity ?? 1), 0
    )
    
    safeGtag('event', 'add_shipping_info', {
      currency,
      value: totalValue,
      items: items.map((item, index) => toGA4Item(item, index)),
      ...(coupon && { coupon }),
      ...(shippingTier && { shipping_tier: shippingTier }),
    })
  },

  addPaymentInfo ({ items, currency = getDefaultCurrency(), value, coupon, paymentType }: AddPaymentInfoParams) {
    const totalValue = value ?? items.reduce((sum, item) => 
      sum + item.price * (item.quantity ?? 1), 0
    )
    
    safeGtag('event', 'add_payment_info', {
      currency,
      value: totalValue,
      items: items.map((item, index) => toGA4Item(item, index)),
      ...(coupon && { coupon }),
      ...(paymentType && { payment_type: paymentType }),
    })
  },

  purchase ({ transactionId, items, currency = getDefaultCurrency(), value, tax, shipping, coupon }: PurchaseParams) {
    const totalValue = value ?? items.reduce((sum, item) => 
      sum + item.price * (item.quantity ?? 1), 0
    )
    
    safeGtag('event', 'purchase', {
      transaction_id: transactionId,
      currency,
      value: totalValue,
      items: items.map((item, index) => toGA4Item(item, index)),
      ...(tax !== undefined && { tax }),
      ...(shipping !== undefined && { shipping }),
      ...(coupon && { coupon }),
    })
  },

  refund ({ transactionId, items, currency = getDefaultCurrency(), value }: RefundParams) {
    safeGtag('event', 'refund', {
      transaction_id: transactionId,
      currency,
      ...(value !== undefined && { value }),
      ...(items && { items: items.map((item, index) => toGA4Item(item, index)) }),
    })
  },

  // ========================================================================
  // Кастомные события
  // ========================================================================

  search ({ searchTerm, resultsCount }: SearchParams) {
    safeGtag('event', 'search', {
      search_term: searchTerm,
      ...(resultsCount !== undefined && { results_count: resultsCount }),
    })
  },

  login ({ method }: LoginParams) {
    safeGtag('event', 'login', {
      method,
    })
  },

  signUp ({ method }: SignUpParams) {
    safeGtag('event', 'sign_up', {
      method,
    })
  },

  addToWishlist ({ item, currency = getDefaultCurrency(), value }: AddToWishlistParams) {
    safeGtag('event', 'add_to_wishlist', {
      currency,
      value: value ?? item.price,
      items: [ toGA4Item(item) ],
    })
  },

  applyPromoCode ({ coupon, success, discount }: ApplyPromoCodeParams) {
    safeGtag('event', 'apply_promo_code', {
      coupon,
      success,
      ...(discount !== undefined && { discount }),
    })
  },

  // ========================================================================
  // Утилиты
  // ========================================================================

  trackEvent (eventName: string, params?: Record<string, unknown>) {
    safeGtag('event', eventName, params)
  },

  setUserId (userId: string | null) {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('set', 'user_id', { user_id: userId })
    }
  },

  setUserProperties (properties: Record<string, unknown>) {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('set', 'user_properties', properties)
    }
  },
}

export default ga4Provider
